import { AssetsFile, BaseLoaderUtil, Characters } from '../../types/asset/asset.types.ts';
import {
  Character,
  CharacterErrorType,
  CharacterGroup,
  CharacterImageExtension,
  CharacterPartKey,
} from '../../types/asset/character.types.ts';
import { PreloadableAsset, PreloadableAssetType } from '../../types/asset/preloading.types.ts';

import { getSentryUtil } from '../monitoring/sentry.util.ts';

import { AssetFilesUtil, getAssetFilesUtil } from './asset-files.util.ts';

export class CharactersUtil implements BaseLoaderUtil {
  private static _instance: CharactersUtil;
  private characters: Characters | null = null;
  private assetFilesUtil: AssetFilesUtil = getAssetFilesUtil();

  private constructor() {
    // Private constructor to enforce singleton pattern
  }

  public static get instance(): CharactersUtil {
    if (!CharactersUtil._instance) {
      CharactersUtil._instance = new CharactersUtil();
    }
    return CharactersUtil._instance;
  }

  public load(assets: AssetsFile): void {
    this.characters = assets['animal-characters'];
    console.log(`Loaded ${Object.keys(this.characters).length} characters`);
  }

  public getCharacter(character: string): Character | undefined {
    return this.characters?.[character];
  }

  public getCharacterOrFallback(character: string): Character {
    if (!this.characters) {
      this.handleError(CharacterErrorType.NotLoaded);
      throw new Error(CharacterErrorType.NotLoaded);
    }
    const characterData = this.getCharacter(character);
    if (!characterData) {
      this.handleError(CharacterErrorType.CharacterNotFound, character);
      return this.getDefaultCharacter();
    }
    return characterData;
  }

  public getDefaultCharacter(): Character {
    if (!this.characters) {
      this.handleError(CharacterErrorType.NotLoaded);
      throw new Error(CharacterErrorType.NotLoaded);
    }
    return Object.values(this.characters)[0];
  }

  public getCharacterImageUrl(
    character: string,
    part: CharacterPartKey,
    fileType: CharacterImageExtension = CharacterImageExtension.Png
  ): string {
    const characterData = this.getCharacterOrFallback(character);
    return this.getCharacterPartUrl(
      characterData.characterGroup,
      characterData.characterType ?? character,
      characterData[part],
      fileType
    );
  }

  public getCharacterGifUrl(character: string): string {
    const characterData = this.getCharacterOrFallback(character);
    return this.getCharacterPartUrl(
      characterData.characterGroup,
      characterData.characterType ?? character,
      characterData.animatedImage,
      CharacterImageExtension.Gif
    );
  }

  private getCharacterPartUrl(
    group: CharacterGroup,
    character: string,
    partUrl: string,
    fileType: CharacterImageExtension
  ): string {
    return this.assetFilesUtil.getS3Url(`img/${group}/${character}/${partUrl}`, fileType);
  }

  public getAssets(characterKey?: string): PreloadableAsset[] {
    if (!this.characters) {
      return [];
    }

    if (characterKey) {
      const character = this.getCharacter(characterKey);
      if (!character) {
        return [];
      }
      return this.getCharacterAssets(characterKey, character);
    }

    return Object.entries(this.characters).flatMap(([key, character]) => this.getCharacterAssets(key, character));
  }

  public getCriticalAssets(): PreloadableAsset[] {
    if (!this.characters) {
      return [];
    }

    return Object.entries(this.characters).map(([key, character]) => ({
      type: PreloadableAssetType.Image,
      url: this.getCharacterPartUrl(character.characterGroup, key, character.headImage, CharacterImageExtension.Png),
    }));
  }

  private getCharacterAssets(characterKey: string, character: Character): PreloadableAsset[] {
    return [
      {
        type: PreloadableAssetType.Image,
        url: this.getCharacterPartUrl(
          character.characterGroup,
          characterKey,
          character.headImage,
          CharacterImageExtension.Png
        ),
      },
      {
        type: PreloadableAssetType.Image,
        url: this.getCharacterPartUrl(
          character.characterGroup,
          characterKey,
          character.pawImage,
          CharacterImageExtension.Png
        ),
      },
      {
        type: PreloadableAssetType.Image,
        url: this.getCharacterPartUrl(
          character.characterGroup,
          characterKey,
          character.animatedImage,
          CharacterImageExtension.Gif
        ),
      },
    ];
  }

  private handleError(errorType: CharacterErrorType, character?: string): void {
    getSentryUtil().trackError(new Error(errorType), { character });
  }
}

// Export a singleton instance
export const getCharactersUtil = (): CharactersUtil => CharactersUtil.instance;
